import type { Keyword } from "@/types/keyword";

/**
 * One row of the Keywords tab's bank picker table. Built from the hero
 * product's keyword bank plus the theme map (WEB-006); this module only
 * decides which rows show, in what order, and which the typeahead suggests.
 *
 * `source` is the raw bank row the mapper started from, carried unchanged
 * (same object reference) so the picker's cells -- `keywordToKpsBreakdown`,
 * `UtilizationCell`, `SourceCell`, the Kw. Sales / ASIN Density formatters,
 * the tracked flag -- can read whatever `Keyword` fields they need without
 * this module re-deriving them.
 */
export type BankPickerRow = {
  keywordId: number;
  keyword: string;
  rank: number | null;
  searchVolume: number | null;
  isMaster: boolean;
  theme: string | null;
  kps: number | null;
  competition: number | null;
  relevance: number | null;
  trend: number | null;
  source: Keyword;
};

export type BankPickerListFilter = "master" | "bank";

export type BankPickerSortKey =
  | "keyword"
  | "rank"
  | "kps"
  | "searchVolume"
  | "competition"
  | "relevance"
  | "trend";

export type BankPickerSort = { key: BankPickerSortKey; dir: "desc" | "asc" } | null;

export type SelectBankPickerRowsOptions = {
  query: string;
  list: Set<BankPickerListFilter>;
  themes: Set<string>;
  sort: BankPickerSort;
};

const RANK_FLOOR = 50;
const TYPEAHEAD_CAP = 8;

function opportunity(row: BankPickerRow): number {
  const rank = Math.min(row.rank ?? RANK_FLOOR, RANK_FLOOR);
  return (row.searchVolume ?? 0) * rank;
}

/**
 * Descending by opportunity, ties broken by original position -- a stable
 * sort, since `Array.prototype.sort` in the runtimes this project targets is
 * stable, but the explicit index tie-break keeps that true regardless.
 */
function byOpportunityDesc(rows: BankPickerRow[]): BankPickerRow[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => opportunity(b.row) - opportunity(a.row) || a.index - b.index)
    .map((entry) => entry.row);
}

/**
 * Master rows and bank rows, each already in opportunity order, woven
 * starting with a master row (master, bank, master, ...). When one side
 * runs out, the rest of the other follows in its own opportunity order.
 */
function interleaveByList(rows: BankPickerRow[]): BankPickerRow[] {
  const master = rows.filter((row) => row.isMaster);
  const bank = rows.filter((row) => !row.isMaster);
  const result: BankPickerRow[] = [];
  const max = Math.max(master.length, bank.length);
  for (let i = 0; i < max; i++) {
    if (i < master.length) result.push(master[i]);
    if (i < bank.length) result.push(bank[i]);
  }
  return result;
}

function matchesQuery(row: BankPickerRow, normalizedQuery: string): boolean {
  return normalizedQuery === "" || row.keyword.toLowerCase().includes(normalizedQuery);
}

function matchesList(row: BankPickerRow, list: Set<BankPickerListFilter>): boolean {
  if (list.size === 0) return true;
  return (list.has("master") && row.isMaster) || (list.has("bank") && !row.isMaster);
}

function matchesThemes(row: BankPickerRow, themes: Set<string>): boolean {
  if (themes.size === 0) return true;
  return row.theme !== null && themes.has(row.theme);
}

function sortKeyValue(row: BankPickerRow, key: BankPickerSortKey): number | string | null {
  switch (key) {
    case "keyword":
      return row.keyword.toLowerCase();
    case "rank":
      return row.rank;
    case "kps":
      return row.kps;
    case "searchVolume":
      return row.searchVolume;
    case "competition":
      return row.competition;
    case "relevance":
      return row.relevance;
    case "trend":
      return row.trend;
  }
}

function compareValues(a: number | string | null, b: number | string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  if (typeof a === "string" && typeof b === "string") return a.localeCompare(b);
  return (a as number) - (b as number);
}

/**
 * Applied last, to the full (uncapped) filtered/ordered rows. Stable: ties
 * (including a `null` vs `null` tie) keep the incoming order. `sort: null` is
 * the identity.
 */
function applySort(rows: BankPickerRow[], sort: BankPickerSort): BankPickerRow[] {
  if (sort === null) return rows;
  const { key, dir } = sort;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const cmp = compareValues(sortKeyValue(a.row, key), sortKeyValue(b.row, key));
      const directed = dir === "asc" ? cmp : -cmp;
      return directed !== 0 ? directed : a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * Decides the Keywords tab bank picker's rows: filters by search text,
 * master/bank list and theme, orders by opportunity, interleaves master and
 * bank rows when nothing narrows the set (across the whole bank -- no cap),
 * then applies the column sort. Paging the result is the caller's job
 * (`KeywordBankPicker`'s `usePagination`). Every returned row is the same
 * object as its input row.
 */
export function selectBankPickerRows(
  rows: BankPickerRow[],
  { query, list, themes, sort }: SelectBankPickerRowsOptions
): BankPickerRow[] {
  const normalizedQuery = query.trim().toLowerCase();
  const hasListOrThemeFilter = list.size > 0 || themes.size > 0;

  const filtered = rows.filter(
    (row) =>
      matchesQuery(row, normalizedQuery) && matchesList(row, list) && matchesThemes(row, themes)
  );

  const ordered = byOpportunityDesc(filtered);

  const positioned = normalizedQuery === "" && !hasListOrThemeFilter ? interleaveByList(ordered) : ordered;

  return applySort(positioned, sort);
}

/**
 * The Keywords tab search bar's typeahead: substring matches, master-list
 * rows first, de-duplicated case-insensitively on `keyword`, capped at 8. An
 * empty query suggests nothing.
 */
export function selectTypeaheadSuggestions(rows: BankPickerRow[], query: string): BankPickerRow[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery === "") return [];

  const matches = rows.filter((row) => row.keyword.toLowerCase().includes(normalizedQuery));
  const master = matches.filter((row) => row.isMaster);
  const bank = matches.filter((row) => !row.isMaster);

  const seen = new Set<string>();
  const result: BankPickerRow[] = [];
  for (const row of [...master, ...bank]) {
    const dedupeKey = row.keyword.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    result.push(row);
    if (result.length === TYPEAHEAD_CAP) break;
  }
  return result;
}
